"use client";

/**
 * ReviewTopicFilter — Phase 8 step 8 deliverable.
 *
 * Plan ref: ~/.claude/plans/remember-we-are-fridge-com-cozy-truffle.md
 *           (search "ReviewTopicFilter" / "fin_review_topic_index"
 *            / "Phase 8 step 8")
 *
 * Renders a dropdown of reviewer-mentioned components above the
 * <CustomerReviews> block. Selecting a topic constrains the visible reviews
 * to those whose body matched that component's keyword variants — purely a
 * client-side subset of the in-memory reviews array (no per-click round
 * trip).
 *
 * Render gates (Real-or-Hidden Rule 3):
 *   - total captured reviews >= 10
 *   - >= 2 distinct topics each have >= 2 matching reviews
 *   - per-topic chips with match_count < 2 are hidden
 *
 * Brand policy: chip / option labels use display_name from
 * fin_component_dictionary — NEVER includes retailer SKU / ASIN / model.
 *
 * Data shape: callers fetch from fin_review_topic_index (joined to
 * fin_component_dictionary for display_name) once at PDP build, then pass
 * the topicIndex array down. The filter does NOT re-scan reviews.
 *
 * Selection state lives in the parent so <CustomerReviews> can constrain
 * the rendered review list. The "All" option clears the selection.
 */

export interface ReviewTopicEntry {
  component_key: string;
  display_name: string;
  match_count: number;
  matched_review_ids: string[];
  /** LLM-free per-topic sentiment (2026-07 sprint): average star rating of
   * the matched reviews that carried a rating, and how many did. Chips show
   * the rating only when rated_count >= MIN_RATED_FOR_SENTIMENT. */
  avg_rating?: number | null;
  rated_count?: number | null;
}

interface ReviewTopicFilterProps {
  /** Pre-fetched topic index rows for this FIN, ordered however the caller wants. */
  topicIndex: ReviewTopicEntry[] | null | undefined;
  /** Total count of reviews actually in memory for this PDP (post NEW-only filter). */
  totalReviews: number;
  /** Currently selected component_key, or null when "All" is active. */
  selectedTopicId: string | null;
  /** Caller updates state when a chip / option is picked. */
  onSelect: (componentKey: string | null) => void;
  className?: string;
}

const MIN_TOTAL_REVIEWS = 10;
const MIN_MATCHES_PER_TOPIC = 2;
const MIN_DISTINCT_TOPICS = 2;
// Sentiment floor: an average over 1-2 ratings is an anecdote, not a signal.
const MIN_RATED_FOR_SENTIMENT = 3;

/** Chip sentiment label, or null below the floor (exported for tests). */
export function topicSentiment(t: ReviewTopicEntry): string | null {
  if (
    typeof t.avg_rating !== 'number' ||
    !Number.isFinite(t.avg_rating) ||
    (t.rated_count ?? 0) < MIN_RATED_FOR_SENTIMENT
  ) {
    return null;
  }
  return `${t.avg_rating.toFixed(1)}★`;
}

/**
 * Decide whether to render the filter at all. The threshold is:
 *   - total reviews >= MIN_TOTAL_REVIEWS, AND
 *   - at least MIN_DISTINCT_TOPICS topics with >= MIN_MATCHES_PER_TOPIC each.
 *
 * Exported so tests / callers can echo the gate in their own UI flow if
 * needed (e.g., to suppress a wrapper section title).
 */
export function shouldRenderReviewTopicFilter(
  topicIndex: ReviewTopicEntry[] | null | undefined,
  totalReviews: number
): boolean {
  if (!topicIndex || totalReviews < MIN_TOTAL_REVIEWS) return false;
  const eligible = topicIndex.filter((t) => t.match_count >= MIN_MATCHES_PER_TOPIC);
  return eligible.length >= MIN_DISTINCT_TOPICS;
}

export function ReviewTopicFilter({
  topicIndex,
  totalReviews,
  selectedTopicId,
  onSelect,
  className = '',
}: ReviewTopicFilterProps) {
  if (!shouldRenderReviewTopicFilter(topicIndex, totalReviews)) return null;

  // Already gated by shouldRender; safe to assume non-null.
  const eligible = (topicIndex ?? [])
    .filter((t) => t.match_count >= MIN_MATCHES_PER_TOPIC)
    // Stable sort: highest match_count first, then alphabetical for ties.
    .sort((a, b) => {
      if (a.match_count !== b.match_count) return b.match_count - a.match_count;
      return a.display_name.localeCompare(b.display_name);
    })
    // Owner QA 07-06: don't spam pills — top 5 topics only.
    .slice(0, 5);

  return (
    <section
      className={`rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4 my-4 ${className}`}
      aria-label="Filter reviews by topic"
      data-testid="review-topic-filter"
      data-topic-count={eligible.length}
      data-selected-topic={selectedTopicId ?? 'all'}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-xs sm:text-sm font-semibold text-gray-900">
          Filter reviews by topic:
        </span>

        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Topic options">
          <button
            type="button"
            onClick={() => onSelect(null)}
            aria-pressed={selectedTopicId === null}
            data-topic-key="all"
            className={
              selectedTopicId === null
                ? 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-[#0469f0] text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40'
                : 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white text-gray-700 border border-gray-200 hover:border-[#0469f0]/40 hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40'
            }
          >
            All
          </button>

          {eligible.map((t) => {
            const isSelected = selectedTopicId === t.component_key;
            const sentiment = topicSentiment(t);
            return (
              <button
                key={t.component_key}
                type="button"
                onClick={() => onSelect(isSelected ? null : t.component_key)}
                aria-pressed={isSelected}
                data-topic-key={t.component_key}
                data-match-count={t.match_count}
                title={
                  sentiment
                    ? `${t.display_name}: mentioned in ${t.match_count} reviews, averaging ${t.avg_rating!.toFixed(1)} stars`
                    : `${t.display_name}: mentioned in ${t.match_count} reviews`
                }
                className={
                  isSelected
                    ? 'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#0469f0] text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40'
                    : 'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-white text-gray-700 border border-gray-200 hover:border-[#0469f0]/40 hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40'
                }
              >
                <span>{t.display_name}</span>
                <span className={isSelected ? 'opacity-90' : 'text-gray-500'}>
                  ({t.match_count})
                </span>
                {sentiment && (
                  <span
                    className={
                      isSelected
                        ? 'opacity-90 font-semibold'
                        : (t.avg_rating as number) >= 4
                          ? 'text-green-700 font-semibold'
                          : (t.avg_rating as number) >= 3
                            ? 'text-yellow-700 font-semibold'
                            : 'text-orange-700 font-semibold'
                    }
                    data-testid="topic-sentiment"
                  >
                    {sentiment}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Pure helper — given the topic index + the current selection, return the
 * subset of review IDs that should remain visible. Returns null when no
 * filter is active (caller renders all reviews unchanged).
 */
export function selectedReviewIdSet(
  topicIndex: ReviewTopicEntry[] | null | undefined,
  selectedTopicId: string | null
): Set<string> | null {
  if (!selectedTopicId || !topicIndex) return null;
  const hit = topicIndex.find((t) => t.component_key === selectedTopicId);
  if (!hit) return null;
  return new Set(hit.matched_review_ids);
}

export default ReviewTopicFilter;
